import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import socket from '../socket';
import useSocketEvent from '../utils/useSocketEvent';
import {
    createOrUpdatePlayset,
    deletePlayset,
    duplicatePlayset,
    loadActiveShow,
    loadPlaysets,
    setActiveShow,
    updatePlaysetStatus,
} from '../integrations/quizApiAdapter';

const STATUS_LABEL = {
    draft: 'Entwurf',
    ready: 'Bereit',
    archived: 'Archiviert',
};

function statusChipClass(status) {
    if (status === 'ready') return 'chip';
    if (status === 'archived') return 'chip muted';
    return 'chip muted';
}

function ShowsView() {
    const navigate = useNavigate();
    const [shows, setShows] = useState([]);
    const [activePlaysetId, setActivePlaysetId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [busyId, setBusyId] = useState(null);
    const [showArchived, setShowArchived] = useState(false);
    const [newName, setNewName] = useState('');
    const [newDescription, setNewDescription] = useState('');
    const [creating, setCreating] = useState(false);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const [showsData, activeData] = await Promise.all([
                loadPlaysets(),
                loadActiveShow(),
            ]);
            setShows(Array.isArray(showsData) ? showsData : []);
            setActivePlaysetId(activeData?.playsetId ?? null);
        } catch (err) {
            setError(err?.message || 'Fehler beim Laden der Shows.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    const onShowActiveChange = useCallback((payload) => {
        setActivePlaysetId(payload?.playsetId ?? null);
    }, []);
    useSocketEvent(socket, 'show:active', onShowActiveChange);

    const visibleShows = useMemo(() => {
        if (showArchived) return shows;
        return shows.filter((s) => s.status !== 'archived');
    }, [shows, showArchived]);

    const handleActivate = async (id) => {
        setBusyId(id);
        try {
            const res = await setActiveShow(id);
            setActivePlaysetId(res?.playsetId ?? String(id));
        } catch (err) {
            setError(err?.message || 'Fehler beim Aktivieren.');
        } finally {
            setBusyId(null);
        }
    };

    const handleStatusChange = async (id, status) => {
        setBusyId(id);
        try {
            const updated = await updatePlaysetStatus(id, status);
            setShows((prev) => prev.map((s) => (s.id === id ? { ...s, ...updated } : s)));
        } catch (err) {
            setError(err?.message || 'Fehler beim Aktualisieren des Status.');
        } finally {
            setBusyId(null);
        }
    };

    const handleDuplicate = async (id) => {
        setBusyId(id);
        try {
            const created = await duplicatePlayset(id);
            setShows((prev) => [...prev, created]);
        } catch (err) {
            setError(err?.message || 'Fehler beim Duplizieren.');
        } finally {
            setBusyId(null);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Show wirklich löschen? Matrix und Finale werden mit gelöscht.')) {
            return;
        }
        setBusyId(id);
        try {
            await deletePlayset(id);
            setShows((prev) => prev.filter((s) => s.id !== id));
        } catch (err) {
            setError(err?.message || 'Fehler beim Löschen.');
        } finally {
            setBusyId(null);
        }
    };

    const handleCreate = async (event) => {
        event.preventDefault();
        if (!newName.trim()) return;
        setCreating(true);
        try {
            const created = await createOrUpdatePlayset({
                name: newName.trim(),
                description: newDescription.trim() || null,
                status: 'draft',
            });
            setShows((prev) => [...prev, created]);
            setNewName('');
            setNewDescription('');
            navigate(`/dashboard/shows/${created.id}/fragen`);
        } catch (err) {
            setError(err?.message || 'Fehler beim Anlegen der Show.');
        } finally {
            setCreating(false);
        }
    };

    const activeShow = useMemo(
        () => shows.find((s) => String(s.id) === String(activePlaysetId)) || null,
        [shows, activePlaysetId]
    );

    return (
        <div className="page section-stack">
            <div className="card section-stack">
                <div className="card-header">
                    <h2 className="card-title">Shows</h2>
                    <span className="text-muted">
                        Eine Show besteht aus Fragenpool, Matrix und Finalspiel.
                    </span>
                </div>
                {activeShow ? (
                    <div
                        className="active-show-banner"
                        style={{
                            background: '#1f6feb',
                            color: 'white',
                            padding: '12px',
                            borderRadius: '6px',
                        }}
                    >
                        <strong>Aktive Show:</strong> {activeShow.name}
                        {activeShow.description ? <em> — {activeShow.description}</em> : null}
                    </div>
                ) : (
                    <div className="text-muted">Es ist gerade keine Show aktiv.</div>
                )}
                <form
                    className="layout-two-column"
                    onSubmit={handleCreate}
                    style={{ alignItems: 'flex-end', flexWrap: 'wrap', gap: '12px' }}
                >
                    <div className="input-group" style={{ flex: '1 1 160px' }}>
                        <label>Neue Show</label>
                        <input
                            type="text"
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            placeholder="Name der Show"
                            required
                        />
                    </div>
                    <div className="input-group" style={{ flex: '2 1 200px' }}>
                        <label>Beschreibung (optional)</label>
                        <input
                            type="text"
                            value={newDescription}
                            onChange={(e) => setNewDescription(e.target.value)}
                            placeholder="kurze Notiz"
                        />
                    </div>
                    <button type="submit" className="btn btn-primary" disabled={creating || !newName.trim()}>
                        {creating ? 'Lege an...' : 'Show anlegen'}
                    </button>
                    <button
                        type="button"
                        className={`toggle-button ${showArchived ? 'is-on' : ''}`}
                        role="switch"
                        aria-checked={showArchived}
                        onClick={() => setShowArchived((v) => !v)}
                    >
                        <span className="toggle-indicator" aria-hidden="true" />
                        <span className="toggle-label">Archivierte anzeigen</span>
                    </button>
                </form>
            </div>

            {error && (
                <div className="error-banner" style={{ background: '#fdecea', color: '#b71c1c', padding: '8px', borderRadius: '4px' }}>
                    <span>{error}</span>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={fetchAll} style={{ marginLeft: '8px' }}>
                        Neu laden
                    </button>
                </div>
            )}

            {loading ? (
                <div className="text-muted">Shows werden geladen...</div>
            ) : visibleShows.length === 0 ? (
                <div className="card text-muted">Noch keine Shows vorhanden.</div>
            ) : (
                <ul className="section-stack" style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                    {visibleShows.map((show) => {
                        const isActive = String(show.id) === String(activePlaysetId);
                        const stats = show.stats || {};
                        const matrixInfo = stats.matrix || {};
                        const finaleInfo = stats.finale || {};
                        return (
                            <li
                                key={show.id}
                                className="card"
                                style={{
                                    border: isActive ? '2px solid #1f6feb' : undefined,
                                }}
                            >
                                <div className="card-header">
                                    <strong>{show.name}</strong>
                                    <div className="chip-row">
                                        <span className={statusChipClass(show.status)}>
                                            {STATUS_LABEL[show.status] || show.status}
                                        </span>
                                        {isActive && <span className="chip">Aktiv</span>}
                                        <span className="chip muted">
                                            {show.questionCount ?? 0} Fragen
                                        </span>
                                        <span className="chip muted">
                                            Matrix: {matrixInfo.filledCells ?? 0} /{' '}
                                            {(matrixInfo.rows ?? 0)}×{(matrixInfo.cols ?? 0)}
                                        </span>
                                        <span className="chip muted">
                                            Finale: {finaleInfo.roundsCount ?? 0} Runden
                                            {finaleInfo.kind ? ` (${finaleInfo.kind})` : ''}
                                        </span>
                                    </div>
                                </div>
                                {show.description && (
                                    <div className="text-muted">{show.description}</div>
                                )}
                                <div className="button-row" style={{ marginTop: '8px', flexWrap: 'wrap' }}>
                                    <Link className="btn btn-primary" to={`/dashboard/shows/${show.id}/fragen`}>
                                        Bearbeiten
                                    </Link>
                                    {!isActive ? (
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={() => handleActivate(show.id)}
                                            disabled={busyId === show.id}
                                        >
                                            Aktivieren
                                        </button>
                                    ) : (
                                        <span className="text-muted">— aktiv —</span>
                                    )}
                                    <button
                                        type="button"
                                        className="btn btn-ghost"
                                        onClick={() => handleDuplicate(show.id)}
                                        disabled={busyId === show.id}
                                    >
                                        Duplizieren
                                    </button>
                                    {show.status !== 'archived' ? (
                                        <button
                                            type="button"
                                            className="btn btn-ghost"
                                            onClick={() => handleStatusChange(show.id, 'archived')}
                                            disabled={busyId === show.id || isActive}
                                            title={isActive ? 'Aktive Show kann nicht archiviert werden.' : ''}
                                        >
                                            Archivieren
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            className="btn btn-ghost"
                                            onClick={() => handleStatusChange(show.id, 'draft')}
                                            disabled={busyId === show.id}
                                        >
                                            Wiederherstellen
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="btn btn-ghost"
                                        onClick={() => handleDelete(show.id)}
                                        disabled={busyId === show.id || isActive}
                                        title={isActive ? 'Aktive Show kann nicht gelöscht werden.' : ''}
                                    >
                                        Löschen
                                    </button>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

export default ShowsView;
